"use client";

import Link from "next/link";
import {
  CalendarCheck,
  Clock,
  ExternalLink,
  MapPin,
  Phone,
} from "lucide-react";
import { RomaSubPageHero } from "@/components/roma-editorial";
import { ReservationForm } from "@/components/reservation-form";
import { RomaReservationWidget } from "@/components/roma-reservation-widget";
import { analytics } from "@/lib/analytics";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { buildHomepageCopy } from "@/lib/marketing-location-copy";
import type { RestaurantSiteConfig } from "@/lib/restaurant-platform";
import {
  buildReservationFaqItems,
  buildReservationPrepItems,
} from "./reservation-content";

function mapsUrlFor(siteConfig: RestaurantSiteConfig) {
  return `https://maps.google.com/?q=${encodeURIComponent(
    `${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state} ${siteConfig.address.zip}`,
  )}`;
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;

  if (normalized.length !== 10) {
    return phone;
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

// Word for word as registered in the C62VPGE campaign message flow. Do not
// edit without updating that campaign in Telnyx first.
const ROMA_RESERVATION_SMS_CONSENT = {
  text: "Text me about this reservation. I agree to receive reservation confirmation text messages from Chef's Roma Kitchen. Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP to opt out. Consent is not required to book.",
  privacyHref: "/privacy",
  termsHref: "/terms",
};

const romaReservationPlanningLinks = [
  { href: "/menu", label: "View menu" },
  { href: "/private-events", label: "Private dining" },
  {
    href: "/best/italian-restaurant-southern-highlands-las-vegas",
    label: "Southern Highlands guide",
  },
  {
    href: "/best/italian-restaurant-henderson-nv",
    label: "Henderson guide",
  },
  { href: "/catering", label: "Catering trays" },
  { href: "/contact", label: "Contact" },
] as const;

export default function ReservationsPage() {
  const { siteConfig, location, isDefaultLocation, romaFunnelEnabled } =
    useMarketingLocationContext();
  const mapsUrl = mapsUrlFor(siteConfig);
  const phoneDisplay = formatPhone(siteConfig.contact.phone);
  const reservationFaqs = buildReservationFaqItems(siteConfig);
  const reservationPrepItems = buildReservationPrepItems(siteConfig);
  const homepageCopy = buildHomepageCopy(location);
  const romaReservationLocations = homepageCopy.romaLocations ?? [];
  const isRomaBrand = homepageCopy.brand === "chefs-roma";

  if (!isDefaultLocation) {
    return (
      <div
        className="roma-paper min-h-screen"
        data-roma-dark-subpage={isRomaBrand ? "reservations" : undefined}
      >
        <RomaSubPageHero
          eyebrow={siteConfig.name}
          image={siteConfig.heroImage || siteConfig.ogImage}
          imageAlt={siteConfig.ogImageAlt}
          headingLevel="h1"
          title={
            <>
              Reserve your{" "}
              <span className="italic text-[var(--roma-terracotta)]">
                table.
              </span>
            </>
          }
          description={
            siteConfig.name === "Roma Kitchen"
              ? "Book Southern Highlands online here. Henderson continues with Google Reserve."
              : `Reserve at ${siteConfig.name}, or call the restaurant for same-day help.`
          }
          className="[&>div:last-child]:py-16 md:[&>div:last-child]:py-20"
        />

        <main className="bg-[var(--roma-ivory)] px-5 py-10 sm:px-8 lg:px-12">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-start">
            <section className="min-w-0">
              {romaReservationLocations.length > 0 ? (
                <RomaReservationWidget
                  locations={romaReservationLocations}
                  variant={romaFunnelEnabled ? "dark" : "light"}
                  renderFirstPartyForm={(locationId) =>
                    // Only the host's own platform location can book here: the
                    // venue details below come from this host's siteConfig.
                    locationId === location.id ? (
                      <ReservationForm
                        locationId={locationId}
                        restaurantPhone={siteConfig.contact.phone}
                        smsConsent={ROMA_RESERVATION_SMS_CONSENT}
                        venue={{
                          name: siteConfig.name,
                          phone: siteConfig.contact.phone,
                          address: siteConfig.address,
                          domain: siteConfig.domain,
                          restaurantId: siteConfig.restaurantId,
                        }}
                      />
                    ) : null
                  }
                />
              ) : (
                <div className="p-4 text-[var(--roma-ivory)] sm:p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--roma-terracotta)]">
                    Reservations
                  </p>
                  <h2 className="mt-2 font-serif text-3xl font-normal">
                    Open the reservation book.
                  </h2>
                  <p className="mt-3 max-w-xl text-sm leading-6 text-[var(--roma-ivory)]/72">
                    Reservations are handled through the restaurant's external
                    booking flow. Use the link below, or call for help with
                    larger parties and same-day changes.
                  </p>
                  <a
                    href={siteConfig.reservationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex min-h-12 items-center justify-center bg-[var(--roma-terracotta)] px-5 text-sm font-semibold uppercase tracking-[0.16em] text-[var(--roma-espresso)]"
                    onClick={() => analytics.reservationClicked("external_reservation_page")}
                  >
                    Open reservations
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </div>
              )}
            </section>

            <aside className="space-y-4">
              <div className="border border-[var(--roma-parchment-line)] bg-white p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--roma-terracotta)]">
                  Need help?
                </p>
                <h2 className="mt-2 font-serif text-2xl font-normal text-[var(--roma-espresso)]">
                  Call or find the room.
                </h2>
                <div className="mt-4 grid gap-2">
                  <a
                    href={`tel:${siteConfig.contact.phone}`}
                    onClick={() => analytics.phoneClicked()}
                    className="inline-flex min-h-11 items-center gap-2 border border-[var(--roma-parchment-line)] px-4 text-sm font-semibold text-[var(--roma-espresso)] hover:border-[var(--roma-terracotta)]"
                  >
                    <Phone className="h-4 w-4" />
                    {phoneDisplay}
                  </a>
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => analytics.mapClicked()}
                    className="inline-flex min-h-11 items-center gap-2 border border-[var(--roma-parchment-line)] px-4 text-sm font-semibold text-[var(--roma-espresso)] hover:border-[var(--roma-terracotta)]"
                  >
                    <MapPin className="h-4 w-4" />
                    Directions
                  </a>
                </div>
              </div>

              <div className="border border-[var(--roma-parchment-line)] bg-white p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--roma-terracotta)]">
                  Plan next
                </p>
                <div className="mt-4 grid gap-2 text-sm">
                  {romaReservationPlanningLinks.map((link, index) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className={`inline-flex min-h-11 items-center justify-between text-[var(--roma-espresso)] ${
                        index === romaReservationPlanningLinks.length - 1
                          ? ""
                          : "border-b border-[var(--roma-parchment-line)]"
                      }`}
                    >
                      {link.label}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>

          <ReservationFaqList reservationFaqs={reservationFaqs} />
        </main>
      </div>
    );
  }

  return (
    <div className="roma-paper min-h-screen">
      <div className="hidden sm:block">
        <RomaSubPageHero
          eyebrow={siteConfig.name}
          image={siteConfig.heroImage || siteConfig.ogImage}
          imageAlt={siteConfig.ogImageAlt}
          headingLevel="h1"
          title={
            <>
              Reserve your{" "}
              <span className="italic text-[var(--roma-terracotta)]">
                table.
              </span>
            </>
          }
          description="Book online below, or call the restaurant for special requests."
          className="[&>div:last-child]:py-20 md:[&>div:last-child]:py-24"
        />
      </div>

      <main className="relative bg-[var(--roma-ivory)]">
        <div className="mx-auto max-w-[1440px] px-2.5 sm:px-8 lg:px-12">
          <div className="relative z-20 mx-auto flex max-w-2xl items-start py-2 sm:block sm:-mt-8 sm:py-0 sm:pb-8">
            <section
              id="reservation-form"
              className="w-full overflow-hidden rounded-xl border border-[var(--roma-parchment-line)] bg-white shadow-[0_18px_54px_-42px_rgba(43,28,17,0.55)] sm:rounded-[2rem] sm:shadow-[0_24px_80px_-48px_rgba(43,28,17,0.6)]"
            >
              <div className="bg-[var(--roma-linen)] px-3 py-3 text-center sm:px-8 sm:py-10">
                <div className="mx-auto flex max-w-lg flex-col items-center gap-1.5 sm:gap-4">
                  <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--roma-terracotta)]/12 text-[var(--roma-terracotta)] sm:flex sm:h-11 sm:w-11">
                    <CalendarCheck className="h-4 w-4 sm:h-5 sm:w-5" />
                  </span>
                  <div>
                    <h2 className="font-serif text-xl font-normal tracking-tight text-[var(--roma-espresso)] sm:hidden">
                      Reserve a table
                    </h2>
                    <p className="mt-0.5 text-[11px] leading-4 text-[var(--roma-espresso)]/72 sm:hidden">
                      Takes under a minute. Confirmation is instant.
                    </p>
                    <h2 className="hidden font-serif text-xl font-normal tracking-tight text-[var(--roma-espresso)] sm:block sm:text-4xl">
                      Make a reservation
                    </h2>
                    <p className="mt-2 hidden text-sm leading-6 text-[var(--roma-espresso)]/72 sm:mt-3 sm:block sm:text-base sm:leading-7">
                      Choose your party size, date, and time. Confirmation is instant.
                    </p>
                    <dl className="mt-5 hidden w-full grid-cols-1 gap-x-5 gap-y-3 border-t border-[var(--roma-parchment-line)] pt-5 text-left sm:grid sm:grid-cols-2">
                      {reservationPrepItems.map((item) => (
                        <div key={item.label} className="min-w-0">
                          <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
                            {item.label}
                          </dt>
                          <dd className="mt-1 text-xs leading-5 text-[var(--roma-espresso)]/70 sm:text-sm">
                            {item.detail}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-white px-2.5 py-2 sm:px-8 sm:py-8">
                <ReservationForm
                  restaurantPhone={siteConfig.contact.phone}
                  venue={{
                    name: siteConfig.name,
                    phone: siteConfig.contact.phone,
                    address: siteConfig.address,
                    domain: siteConfig.domain,
                    restaurantId: siteConfig.restaurantId,
                  }}
                />
              </div>

              <div className="hidden border-t border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] px-5 py-4 text-center sm:block sm:px-8">
                <p className="text-sm text-[var(--roma-espresso)]/70">
                  For parties of 8 or more, please call ahead at{" "}
                  <a
                    href={`tel:${siteConfig.contact.phone}`}
                    className="inline-flex min-h-11 items-center font-medium text-[var(--roma-espresso)] underline-offset-4 hover:text-[var(--roma-terracotta)] hover:underline"
                    onClick={() => analytics.phoneClicked()}
                  >
                    {phoneDisplay}
                  </a>
                  .
                </p>
              </div>
            </section>
          </div>

          <div className="mx-auto hidden max-w-md py-8 sm:block sm:py-12">
            <div className="mb-8 flex justify-center gap-3">
              <a
                href={`tel:${siteConfig.contact.phone}`}
                onClick={() => analytics.phoneClicked()}
                className="roma-cta flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium active:scale-[0.98]"
              >
                <Phone className="h-4 w-4" />
                Call Us
              </a>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.mapClicked()}
                className="flex items-center gap-2 rounded-full border border-[var(--roma-espresso)]/20 px-5 py-3 text-sm font-medium text-[var(--roma-espresso)] transition-all hover:bg-[var(--roma-espresso)] hover:text-[var(--roma-ivory)] active:scale-[0.98]"
              >
                <MapPin className="h-4 w-4" />
                Directions
              </a>
            </div>

            <div className="mb-8 text-center">
              <p className="font-serif text-lg italic text-[var(--roma-espresso)]/75">
                <Clock className="-mt-0.5 mr-1.5 inline-block h-3.5 w-3.5" />
                Reservations available for dinner service
              </p>
              <p className="roma-eyebrow mt-3 justify-center text-[var(--roma-terracotta)]">
                <span>For 8 or more, please call ahead</span>
              </p>
            </div>

            <div className="mb-6 border-t border-[var(--roma-parchment-line)]" />

            <div className="flex justify-center gap-6 text-sm">
              <Link className="roma-link inline-flex min-h-11 items-center text-[var(--roma-espresso)]/70 hover:text-[var(--roma-espresso)]" href="/menu">
                Menu
              </Link>
              <Link className="roma-link inline-flex min-h-11 items-center text-[var(--roma-espresso)]/70 hover:text-[var(--roma-espresso)]" href="/events">
                Events
              </Link>
              <Link className="roma-link inline-flex min-h-11 items-center text-[var(--roma-espresso)]/70 hover:text-[var(--roma-espresso)]" href="/private-events">
                Private Dining
              </Link>
            </div>
          </div>

          <ReservationFaqList reservationFaqs={reservationFaqs} />
        </div>
      </main>
    </div>
  );
}

function ReservationFaqList({
  reservationFaqs,
}: {
  reservationFaqs: Array<{ question: string; answer: string }>;
}) {
  return (
    <section className="mx-auto max-w-3xl pb-14 pt-2 sm:pb-18">
      <div className="border-t border-[var(--roma-parchment-line)] pt-9">
        <p className="roma-eyebrow text-[var(--roma-terracotta)]">
          Reservation FAQ
        </p>
        <h2 className="mt-2 font-serif text-3xl font-normal text-[var(--roma-espresso)]">
          Common reservation questions
        </h2>
        <div className="mt-7 divide-y divide-[var(--roma-parchment-line)] border-y border-[var(--roma-parchment-line)]">
          {reservationFaqs.map((faq) => (
            <article key={faq.question} className="py-4 sm:py-5">
              <h3 className="text-[15px] font-semibold leading-snug text-[var(--roma-espresso)] sm:text-base">
                {faq.question}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[var(--roma-espresso)]/72 sm:leading-7 md:text-base">
                {faq.answer}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
